import { API_PREFIX, type ApiErrorBody, type ApiErrorCode } from '@evesix/contracts';

/** The server answered with an error body. */
export class ApiRequestError extends Error {
  override readonly name = 'ApiRequestError';

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

export interface ApiClientOptions {
  /** Origin or path prefix the API lives under, e.g. "" (same origin) or "https://api.example". */
  readonly baseUrl: string;
  readonly timeoutMs?: number;
  readonly fetch?: typeof fetch;
  /** "Bearer" for an operator session; "Tablet" for an enrolled tablet's sync credentials. */
  readonly authScheme?: 'Bearer' | 'Tablet';
}

/**
 * Thin JSON client for `/api/v1`. Holds the operator's bearer token in memory only — a reload
 * means signing in again, which is what a shared kiosk should do.
 */
export class ApiClient {
  #token: string | null = null;
  readonly #baseUrl: string;
  readonly #timeoutMs: number;
  readonly #fetch: typeof fetch;
  readonly #authScheme: 'Bearer' | 'Tablet';

  constructor({
    baseUrl,
    timeoutMs = 8000,
    fetch: fetchImpl,
    authScheme = 'Bearer',
  }: ApiClientOptions) {
    this.#authScheme = authScheme;
    this.#baseUrl = baseUrl.replace(/\/+$/, '');
    this.#timeoutMs = timeoutMs;
    this.#fetch = fetchImpl ?? ((input, init) => fetch(input, init));
  }

  setToken(token: string | null): void {
    this.#token = token;
  }

  get signedIn(): boolean {
    return this.#token !== null;
  }

  get<T>(path: string): Promise<T> {
    return this.#request<T>('GET', path);
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.#request<T>('POST', path, body);
  }

  delete(path: string): Promise<void> {
    return this.#request<void>('DELETE', path);
  }

  async #request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (this.#token) headers.authorization = `${this.#authScheme} ${this.#token}`;

    const response = await this.#fetch(`${this.#baseUrl}${API_PREFIX}${path}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: AbortSignal.timeout(this.#timeoutMs),
    });

    if (response.status === 204) return undefined as T;
    const text = await response.text();
    const json: unknown = text ? JSON.parse(text) : null;
    if (response.ok) return json as T;

    const error = (json ?? {}) as Partial<ApiErrorBody>;
    throw new ApiRequestError(
      response.status,
      error.error ?? 'internal',
      error.message ?? `HTTP ${response.status}`,
      error.retryAfterSeconds,
    );
  }
}
